import { useState } from "react";
import { toast } from "sonner";
import { Settings2, Loader2 } from "lucide-react";
import { api, formatApiError } from "../lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";

const VARIANTS = [["standard", "عادي"], ["advanced", "محترف"]];

/** Manager-only: enable/disable the daily ledger for ONE employee and pick its type. */
export function EmployeeDailySettings({ employee, onSaved }) {
    const [open, setOpen] = useState(false);
    const [enabled, setEnabled] = useState(employee.daily_enabled);
    const [variant, setVariant] = useState(employee.daily_variant || "standard");
    const [busy, setBusy] = useState(false);

    const save = async () => {
        setBusy(true);
        try {
            await api.post(`/auth/company/employees/${employee.id}/daily-settings`, { daily_enabled: enabled, daily_variant: enabled ? variant : null });
            toast.success("تم حفظ إعدادات دفتر الحركة للموظف");
            setOpen(false);
            onSaved?.();
        } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
        finally { setBusy(false); }
    };

    return (
        <>
            <button
                type="button"
                onClick={(e) => { e.preventDefault(); e.stopPropagation(); setOpen(true); }}
                className="inline-flex items-center gap-1.5 rounded-lg border border-neutral-200 bg-white px-2.5 py-1.5 text-xs font-bold text-neutral-700 hover:bg-neutral-50"
                data-testid={`emp-daily-settings-btn-${employee.id}`}
            >
                <Settings2 className="h-3.5 w-3.5" />
                {employee.daily_enabled ? `دفتر الحركة: ${employee.daily_variant === "advanced" ? "محترف" : "عادي"}` : "دفتر الحركة: غير مفعّل"}
            </button>
            <Dialog open={open} onOpenChange={setOpen}>
                <DialogContent dir="rtl" data-testid="emp-daily-settings-dialog" onClick={(e) => e.stopPropagation()}>
                    <DialogHeader className="text-right sm:text-right">
                        <DialogTitle>دفتر الحركة — {employee.company_name}</DialogTitle>
                        <DialogDescription>أنت من يحدد إن كان الموظف يستخدم دفتر الحركة ونوعه. إلغاء التفعيل يخفي الدفتر فقط ولا يحذف السجلات المحفوظة.</DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4">
                        <div className="flex gap-2">
                            {[[true, "مفعّل"], [false, "غير مفعّل"]].map(([v, l]) => (
                                <button key={l} onClick={() => setEnabled(v)} className={`flex-1 rounded-xl border-2 py-2.5 text-sm font-bold ${enabled === v ? "border-amber-700 bg-amber-50 text-amber-900" : "border-neutral-200 text-neutral-600"}`} data-testid={`emp-daily-enabled-${v ? "on" : "off"}`}>{l}</button>
                            ))}
                        </div>
                        {enabled ? (
                            <div>
                                <div className="mb-2 text-sm font-semibold text-neutral-700">نوع دفتر الحركة</div>
                                <div className="flex gap-2">
                                    {VARIANTS.map(([v, l]) => (
                                        <button key={v} onClick={() => setVariant(v)} className={`flex-1 rounded-xl border-2 py-2.5 text-sm font-bold ${variant === v ? "border-amber-700 bg-amber-50 text-amber-900" : "border-neutral-200 text-neutral-600"}`} data-testid={`emp-daily-variant-${v}`}>{l}</button>
                                    ))}
                                </div>
                            </div>
                        ) : null}
                        <button onClick={save} disabled={busy} className="inline-flex h-11 w-full items-center justify-center gap-2 rounded-xl bg-amber-700 text-sm font-bold text-white hover:bg-amber-800 disabled:opacity-60" data-testid="emp-daily-settings-save">
                            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : null} حفظ
                        </button>
                    </div>
                </DialogContent>
            </Dialog>
        </>
    );
}
